<script lang="ts">
  export let uploading = false;
  export let file: File | null = null;
  export let fileId: string | null = null;
  export let onfileSelected: (detail: { file: File }) => void = () => {};

  let dragOver = false;
  let fileInput: HTMLInputElement;
  let previousFileUrl: string | null = null;
  let tiffPreviewUrl: string | null = null;
  let loadingPreview = false;
  let previewGenerated = false;
  let previewError = false;

  function handleDragOver(event: DragEvent) {
    event.preventDefault();
    if (!uploading) {
      dragOver = true;
    }
  }

  function handleDragLeave(event: DragEvent) {
    event.preventDefault();
    dragOver = false;
  }

  function handleDrop(event: DragEvent) {
    event.preventDefault();
    dragOver = false;

    if (uploading) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      file = files[0];
      onfileSelected({ file });
    }
  }

  function handleFileInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (files && files.length > 0) {
      file = files[0];
      onfileSelected({ file });
    }
  }

  function openFileDialog() {
    fileInput?.click();
  }

  import { onDestroy } from 'svelte';
  import config from '../config';

  // Cleanup on component destroy
  onDestroy(() => {
    if (previousFileUrl) {
      URL.revokeObjectURL(previousFileUrl);
    }
  });

  $: fileExtension = file?.name.split('.').pop()?.toLowerCase();
  $: isPreviewableImage = file && ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension || '');
  $: isImageFile = file && ['jpg', 'jpeg', 'png', 'tif', 'tiff', 'gif', 'bmp', 'webp'].includes(fileExtension || '');

   // Cleanup previous object URL when file changes
   $: if (file && fileInput) {
     if (previousFileUrl) {
       URL.revokeObjectURL(previousFileUrl);
       previousFileUrl = null;
     }
     previewError = false;
     if (isPreviewableImage) {
       previousFileUrl = URL.createObjectURL(file);
     }
   }

  // Reset file input when file prop changes to null
  $: if (file === null && fileInput) {
    fileInput.value = '';
    if (previousFileUrl) {
      URL.revokeObjectURL(previousFileUrl);
      previousFileUrl = null;
    }
  }

  async function generateTiffPreview() {
    if (!fileId) return;

    loadingPreview = true;
    previewError = false;
    try {
      const response = await fetch(`${config.backendUrl}/api/preview/${fileId}?max_size=512`);
      const data = await response.json();

      if (data.success) {
        tiffPreviewUrl = data.preview_url;
        previewGenerated = true;
      } else {
        console.error('Preview generation failed:', data);
        previewError = true;
      }
    } catch (error) {
      console.error('Could not generate TIFF preview:', error);
      previewError = true;
    } finally {
      loadingPreview = false;
    }
  }
</script>

<div
  class="upload-zone {dragOver ? 'drag-over' : ''} {uploading ? 'uploading' : ''}"
  on:dragover={handleDragOver}
  on:dragleave={handleDragLeave}
  on:drop={handleDrop}
  on:click={uploading ? undefined : openFileDialog}
  on:keydown={(e) => !uploading && (e.key === 'Enter' || e.key === ' ') && openFileDialog()}
  role="button"
  tabindex={uploading ? -1 : 0}
  aria-label={uploading ? "Upload in progress" : "Upload microscopy image"}
  aria-busy={uploading}
>
  {#if file && isImageFile && !fileId}
    <div class="file-preview">
      {#if isPreviewableImage && previousFileUrl}
        <img src={previousFileUrl} alt="Preview" class="preview-image" />
      {:else if (fileExtension === 'tif' || fileExtension === 'tiff')}
        {#if loadingPreview}
          <div class="preview-loading">
            <div class="spinner-small"></div>
            <p class="loading-text">Generating preview...</p>
          </div>
         {:else if tiffPreviewUrl && !previewError}
           <div class="tiff-preview-container">
             <img src={tiffPreviewUrl} alt="TIFF Preview" class="preview-image" on:error={() => { previewError = true; }} />
             <div class="tiff-overlay">
               <span class="tiff-badge">TIFF</span>
             </div>
           </div>
         {:else}
          <div class="file-icon">
            <svg class="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            <div class="tiff-label">Preview failed</div>
          </div>
        {/if}
      {:else}
        <div class="file-icon">
          <svg class="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
        </div>
      {/if}
      <div class="file-info">
        <p class="file-name">{file.name}</p>
        <p class="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
      </div>
    </div>
  {:else}
    <div class="upload-prompt">
      <svg class="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
      </svg>
      <div class="upload-text">
        {#if uploading}
          <p class="primary-text">Processing...</p>
          <p class="secondary-text">Please wait</p>
        {:else}
          <p class="primary-text">Drop your microscopy image here</p>
          <p class="secondary-text">or click to browse</p>
        {/if}
      </div>
    </div>
  {/if}
</div>

<input
  bind:this={fileInput}
  type="file"
  accept=".tif,.tiff,.png,.jpg,.jpeg"
  on:change={handleFileInput}
  class="hidden"
/>

<style>
  .upload-zone {
    @apply relative border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer transition-all duration-200 hover:border-blue-400 hover:bg-blue-50;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .upload-zone.drag-over {
    @apply border-blue-500 bg-blue-50;
  }

  .upload-zone.uploading {
    @apply border-gray-200 bg-gray-50 cursor-not-allowed;
  }

  .file-preview {
    @apply flex items-center space-x-4 w-full max-w-md;
  }

  .preview-image {
    @apply w-64 h-64 object-cover rounded;
  }

  .file-icon {
    @apply w-16 h-16 flex items-center justify-center bg-gray-100 rounded;
  }

  .icon-svg {
    @apply w-8 h-8 text-gray-400;
  }

  .tiff-label {
    @apply text-xs font-mono text-gray-600 mt-1;
  }

  .file-info {
    @apply text-left flex-1;
  }

  .file-name {
    @apply font-medium text-gray-900 truncate;
  }

  .file-size {
    @apply text-sm text-gray-500;
  }

  .upload-prompt {
    @apply flex flex-col items-center space-y-3;
  }

  .upload-icon {
    @apply w-12 h-12 text-gray-400;
  }

  .upload-text {
    @apply text-center;
  }

  .primary-text {
    @apply text-lg font-medium text-gray-900;
  }

  .secondary-text {
    @apply text-sm text-gray-500;
  }

  .hidden {
    display: none;
  }

  .preview-loading {
    @apply flex flex-col items-center space-y-2 p-4;
  }

  .spinner-small {
    @apply animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full;
  }

  .loading-text {
    @apply text-sm text-gray-600;
  }

  .tiff-preview-container {
    @apply relative;
  }

  .tiff-overlay {
    @apply absolute top-2 right-2;
  }

  .tiff-badge {
    @apply bg-blue-600 text-white text-xs font-mono px-2 py-1 rounded;
  }
</style>